import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router";

import { getFiltersPredicationFromURI } from "components/Filters/helpers";
import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";
import { getSearchQuery } from "components/SearchInput/helpers";

import { SCAN_RESOURCES_FILTER_ITEMS_DICTIONARY } from "../constants";
import { ScanResourcesOutletContext } from "../types";
import { SEARCH_SCAN_RESOURCES_SUGGESTIONS } from "./gql";
import useScanResourcesRefetchOnRunFinish from "./useRefetchOnRunFinish";

const useScanResourcesSuggestions = () => {
  const { run } = useOutletContext<ScanResourcesOutletContext>();
  const [searchParams] = useSearchParams();
  const [activeFields, setActiveFields] = useState<string[] | null>(null);

  const runId = run?.id;
  const fullTextSearch = getSearchQuery(searchParams);

  const predicates = useMemo(
    () => [...(getFiltersPredicationFromURI(searchParams, true)?.values() || [])],
    [searchParams]
  );

  const { data, previousData, loading, refetch } = useQuery(SEARCH_SCAN_RESOURCES_SUGGESTIONS, {
    variables: {
      id: runId || "",
      input: { fields: activeFields, fullTextSearch, predicates },
    },
    nextFetchPolicy: "cache-first",
    skip: !runId,
  });

  useScanResourcesRefetchOnRunFinish(refetch);

  const cachedFiltersData = useCachedFilterFields(data?.scanRun.searchResourcesSuggestions.fields);

  const sortOptions = useMemo(
    (): SortOption[] =>
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => ({ value: field.name, label: field.name })),
    [cachedFiltersData]
  );

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: SCAN_RESOURCES_FILTER_ITEMS_DICTIONARY,
  });

  // Ignoring an empty set keeps the values already fetched once every section collapses.
  const handleRefetchActiveSections = useCallback((fields: string[]) => {
    if (fields.length > 0) {
      setActiveFields(fields);
    }
  }, []);

  return {
    filters,
    filtersMap: filterOptionsMap,
    sortOptions,
    filtersLoading: loading,
    refetchActiveSections: handleRefetchActiveSections,
    filteredCount:
      data?.scanRun.searchResourcesSuggestions.filteredCount ??
      previousData?.scanRun.searchResourcesSuggestions.filteredCount ??
      0,
  };
};

export default useScanResourcesSuggestions;
